import { React, useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import CalcDate from '../../Model/Share/Normal/CalcDate';

import { ReactComponent as Menu2IconSvg } from '../../image/Menu2.svg';
import { ReactComponent as LockIconSvg } from '../../image/Lock.svg';
import { ReactComponent as PlusSvg } from '../../image/Plus.svg';

const LockerInfo = forwardRef((props, ref) => {
  const { 
    index, 
    lockerIndex,
    data, 
    clickFunctionFn, 
    clickUsageHistoryFn, 
    clickChangeStateFn, 
    clickDeleteLockerFn, 
    clickChangeNameFn,
    clickExtendFn
   } = props;
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [clickedMore, setClickedMore] = useState(false);

  const MoreButtonRef = useRef();
  const MoreBoxRef = useRef();
  const HistoryRef = useRef();
  const TextRef = useRef();
  const LockRef = useRef();
  const DeleteRef = useRef();
  const ChangeRef = useRef();
  const MessageRef = useRef();
  const ExtendRef = useRef();

  const history = useHistory();

  useImperativeHandle(ref, () => ({
		setClickedMore: (val) => {
      setClickedMore(val);
    },
    getMoreButtonRef: () => {
      return MoreButtonRef.current;
    },
    getMoreBoxRef: () => {
      return MoreBoxRef.current;
    }
	}));

  // 락커 상태 기반 derived 값 (mutation 없이 매 렌더 계산) //
  const isExpired = data.state === 'Using'
    && !moment(data.endDate).isAfter(moment())
    && moment(data.endDate).format('YYYY.MM.DD') !== moment().format('YYYY.MM.DD');

  const days = data.state === 'Using' ? CalcDate.getDday(data.endDate)
    : data.state === 'Expected' ? '-'
    : '';

  const displayState = data.state === 'Empty' ? t('main.locker.empty')
    : data.state === 'Using' ? (isExpired ? t('main.locker.overused') : t('main.locker.inUse'))
    : data.state === 'Invalid' ? t('main.locker.unavailable')
    : data.state === 'Expected' ? t('main.locker.expected')
    : '';

  const btnText = data.state === 'Empty' ? t('main.locker.startUsage')
    : data.state === 'Using' ? t('main.locker.endUsage')
    : t('main.locker.unlock');
  
  useEffect(() => {
    if (clickedMore) {
      MoreBoxRef.current.style.visibility = 'visible';
      MoreBoxRef.current.style.border = `1px solid ${theme.primary}`;
      if (data.state === 'Empty') MoreBoxRef.current.style.height = `144px`;
      else if (data.state === 'Using') MoreBoxRef.current.style.height = `144px`;
      else MoreBoxRef.current.style.height = `108px`;
    }
    else {
      MoreBoxRef.current.style.visibility = 'hidden';
      MoreBoxRef.current.style.border = `0px`;
      MoreBoxRef.current.style.height = `0px`;
    }
  }, [clickedMore, data])
  
  useEffect(() => {
    document.addEventListener("mousedown", onClickOutSide);

    return () => {
      document.removeEventListener("mousedown", onClickOutSide);
    }
  }, [])

  function onClickOutSide(e) {
    if (MoreBoxRef.current !== e.target 
      && MoreButtonRef.current !== e.target
      && HistoryRef.current !== e.target
      && TextRef.current !== e.target
      && LockRef.current !== e.target
      && DeleteRef.current !== e.target
      && ChangeRef.current !== e.target
      && ExtendRef.current !== e.target) {
      setClickedMore(false);
    }
  }

  function MoreButtonClick() {
    if (data.state === 'Expected') {
      alert(t('main.locker.paymentRequired'));
      return;
    }

    if (clickedMore) setClickedMore(false);
    else setClickedMore(true);
  }

  // 사용자 이름 및 D-day클릭시
  function onClickMidLineWrapper() {
    if (data.memberId === 0) return;

    window.open('/member/valid/detail/' + data.memberId + '/locker', '_blank');
  }

  // 락커 이름을 "이름 + 좌표 suffix(A-1 또는 A-1(2) 같은)"로 분리 //
  function parseLockerName(name) {
    const match = (name || '').match(/^(.*?)(\s+[A-Z]-\d+(?:\(\d+\))?)$/);
    if (match) return { prefix: match[1], suffix: match[2] };
    return { prefix: name || '', suffix: '' };
  }

  // D-Day 검사 //
  function checkDday() {
    if (days === '') return '';
    else if (days === '-') return;
    else if (moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'day') > 0) return 'D-' + moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'day');
    else if (moment(data.endDate).format('YYYY.MM.DD') === moment().format('YYYY.MM.DD')) return 'D-Day';
    else return 'D+' + moment(moment().format('YYYY-MM-DD')).diff(moment(moment(data.endDate).format('YYYY-MM-DD')), 'day');
  }

  return (
    <TopLevelWrapper>
      <InfoBox $state={data.state} $isExpired={isExpired}>
        {
          data.state !== 'Invalid' &&
          <InfoBoxInnerWrapper>
            <TopWrapper>
              <TopLineWrapper>
                <NumberWrapper>
                  <Number>{parseLockerName(data.lockerName).prefix}</Number>
                  {parseLockerName(data.lockerName).suffix && <NumberSuffix>{parseLockerName(data.lockerName).suffix}</NumberSuffix>}
                </NumberWrapper>
                <MenuIcon ref={MoreButtonRef} onClick={MoreButtonClick}/>
              </TopLineWrapper>
              {
                data.state !== 'Empty' &&
                <MidLineWrapper onClick={onClickMidLineWrapper}>
                  <NameWrapper>
                    <Name>{data.userName}</Name>
                  </NameWrapper>
                  <Dday $isOverused={isExpired}>{checkDday()}</Dday>
                </MidLineWrapper>
              }
            </TopWrapper>
            <BottomLineWrapper>
              {
                data.state === 'Invalid' &&
                <LockIcon />
              }
              {
                data.state !== 'Empty' ?
                <State $state={data.state} $isExpired={isExpired}>{displayState}</State>
                :
                <LockerUsagePlusButton onClick={() => clickFunctionFn(index, lockerIndex)}>
                  <PlusIcon />
                </LockerUsagePlusButton>
              }
            </BottomLineWrapper>
          </InfoBoxInnerWrapper>
        }
      </InfoBox>
      
      <MoreBox ref={MoreBoxRef}>
        <MoreButton ref={HistoryRef} onClick={() => clickUsageHistoryFn(index, lockerIndex)}>{t('main.locker.usageHistory')}</MoreButton>
        { data.state !== 'Empty' && <MoreButton ref={TextRef} onClick={() => clickFunctionFn(index, lockerIndex)}>{btnText}</MoreButton> }
        { data.state === 'Empty' && <MoreButton ref={LockRef} onClick={() => clickChangeStateFn(index, lockerIndex)}>{t('main.locker.lockLocker')}</MoreButton> }
        { data.state === 'Empty' && <MoreButton ref={DeleteRef} onClick={() => clickDeleteLockerFn(index, lockerIndex)}>{t('main.locker.deleteLocker')}</MoreButton> }
        <MoreButton ref={ChangeRef} onClick={() => clickChangeNameFn(index, lockerIndex)}>{t('main.locker.changeName')}</MoreButton>
        { data.state === 'Using' && <MoreButton ref={ExtendRef} onClick={() => clickExtendFn(index, lockerIndex)}>{t('main.locker.extendUsage')}</MoreButton> }
      </MoreBox>
    </TopLevelWrapper>
  )
});

export default LockerInfo;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 10px;

  box-sizing: border-box;
  width: 122px;
  height: 122px;

  border: ${props => {
    if (props.$state === 'Invalid') return 'none';
    if (props.$state === 'Using' && props.$isExpired) return `1px solid ${props.theme.pointRed}`;
    if (props.$state === 'Using') return `1px solid ${props.theme.line}`;
    return `1px solid ${props.theme.line}`;
  }};
  border-radius: 12px;

  transition: all 0.3s ease-in-out;
`;

const InfoBoxInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: space-between;

  width: 100%;
  height: 100%;
`;

const TopWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
`;

const TopLineWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 15px;

  gap: 5px;

  overflow: hidden;
`;

const NumberWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const Number = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  @media (hover: hover) {
    &:hover {
      cursor: default;
    }
  }
`;

const NumberSuffix = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;

  flex-shrink: 0;
  white-space: nowrap;
`;

const MenuIcon = styled(Menu2IconSvg)`
  width: 4px;
  height: 13px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.subText};
    transition: fill 0.3s ease-in-out;
  }

  @media (hover: hover) {
    &:hover {
      cursor: pointer;

      & path {
        fill: ${props => props.theme.primary};
      }
    }
  }
`;

const MidLineWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: space-between;
  align-items: flex-start;

  margin-top: 6px;

  width: 100%;
  height: 46px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;
  
  width: 100%;
  height: 20px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }

  overflow: hidden;
`;

const Name = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const Dday = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.$isOverused ? props.theme.pointRed : props.theme.text};

  margin: 0 0 0 0;
`;

const BottomLineWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const PlusIcon = styled(PlusSvg)`
  width: 40px;
  height: 40px;

  flex-shrink: 0;

  color: ${props => props.theme.line};
`;

const LockerUsagePlusButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 77px;

  border-radius: 5px;
  background-color: ${props => props.theme.buttonFill};
`;


const LockIcon = styled(LockIconSvg)`
  width: 11px;
  height: 14px;

  flex-shrink: 0;
  margin-right: 4px;

  & path {
    fill: ${props => props.theme.pointRed};
  }
`;

const State = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;
  color: ${props => {
    if (props.$state === 'Using' && props.$isExpired) return props.theme.pointRed;
    if (props.$state === 'Using') return props.theme.subKey;
    if (props.$state === 'Invalid') return props.theme.pointRed;
    return props.theme.text;
  }};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: default;
    }
  }
`;

const MoreBox = styled.div`
  position: absolute;

  display: flex;
  flex-direction: column;
  overflow: hidden;

  justify-content: center;
  align-items: center;

  width: 78px;
  height: 0px;

  margin-top: 35px;
  margin-left: 60px;

  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};

  visibility: hidden;

  transition: all 0.3s ease-in-out;
`;

const MoreButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 78px;
  height: 36px;

  background-color: ${props => props.theme.subBackground};

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.primary};

  margin: 0 0 0 0;

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.primary};
      color: ${props => props.theme.subBackground};
    }
  }
`;